import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const ReporteGeneral = () => {
  const [socios, setSocios] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMes, setFilterMes] = useState(new Date().getMonth() + 1);
  const [filterAño, setFilterAño] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchSocios();
    fetchRegistros();
  }, [filterMes, filterAño]);

  const fetchSocios = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistros = async () => {
    try {
      const response = await axios.get(`/api/registros-mensuales?mes=${filterMes}&año=${filterAño}`);
      setRegistros(response.data.data || []);
    } catch (error) {
      console.error('Error fetching registros:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  const obtenerRegistroSocio = (socioId) => {
    return registros.find(r => r.socio._id === socioId);
  };

  // Todo lo recaudado en un registro mensual
  const totalDeRegistro = (r) =>
    (r?.aportes || 0) + (r?.capitalPrestamo || 0) + (r?.interesPrestamo || 0) +
    (r?.paseo || 0) + (r?.programa || 0) + (r?.multas || 0);

  const calcularTotalesGenerales = () => {
    return {
      totalAportes: registros.reduce((sum, r) => sum + (r.aportes || 0), 0),
      totalCapital: registros.reduce((sum, r) => sum + (r.capitalPrestamo || 0), 0),
      totalInteres: registros.reduce((sum, r) => sum + (r.interesPrestamo || 0), 0),
      totalCuotaPrestamo: registros.reduce((sum, r) => sum + ((r.capitalPrestamo || 0) + (r.interesPrestamo || 0)), 0),
      totalPaseo: registros.reduce((sum, r) => sum + (r.paseo || 0), 0),
      totalPrograma: registros.reduce((sum, r) => sum + (r.programa || 0), 0),
      totalMultas: registros.reduce((sum, r) => sum + (r.multas || 0), 0),
      totalGeneral: registros.reduce((sum, r) => sum + totalDeRegistro(r), 0),
      sociosConRegistro: registros.length,
      sociosSinRegistro: socios.length - registros.length
    };
  };

  const handleExportarPDF = () => {
    toast.info('Función de exportar PDF en desarrollo');
  };

  const handleExportarExcel = () => {
    const filas = [
      [`Reporte General de Valores - ${getNombreMes(filterMes)} ${filterAño}`],
      ['Socio', 'Cédula', 'Número', 'Aportes', 'Capital', 'Interés', 'Paseo', 'Programa', 'Multas', 'Total']
    ];
    socios.forEach(socio => {
      const r = obtenerRegistroSocio(socio._id);
      filas.push([socio.name, socio.cedula, socio.socioInfo?.numeroSocio || '', r?.aportes || 0, r?.capitalPrestamo || 0,
        r?.interesPrestamo || 0, r?.paseo || 0, r?.programa || 0, r?.multas || 0, totalDeRegistro(r)]);
    });
    const t = calcularTotalesGenerales();
    filas.push([], ['TOTAL', '', '', t.totalAportes, t.totalCapital, t.totalInteres, t.totalPaseo, t.totalPrograma, t.totalMultas, t.totalGeneral]);
    const csv = filas.map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-general-${filterAño}-${String(filterMes).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImpresion = () => {
    window.print();
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  const totales = calcularTotalesGenerales();

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-chart-pie me-2"></i>
                      Reporte General de Valores
                    </h2>
                    <p className="text-muted mb-0">
                      Resumen consolidado de todos los socios por mes
                    </p>
                  </div>
                  <div>
                    <Button variant="outline-info" size="sm" onClick={handleImpresion} className="me-2">
                      <i className="fas fa-print me-1"></i>
                      Imprimir
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={handleExportarPDF} className="me-2">
                      <i className="fas fa-file-pdf me-1"></i>
                      PDF
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={handleExportarExcel}>
                      <i className="fas fa-file-excel me-1"></i>
                      Excel
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Mes
                  </Form.Label>
                  <Form.Select value={filterMes} onChange={(e) => setFilterMes(parseInt(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(mes => (
                      <option key={mes} value={mes}>
                        {getNombreMes(mes)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Año
                  </Form.Label>
                  <Form.Select value={filterAño} onChange={(e) => setFilterAño(parseInt(e.target.value))}>
                    {Array.from({ length: 16 }, (_, i) => 2020 + i).map(año => (
                      <option key={año} value={año}>{año}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tarjetas de Resumen */}
        <Row className="mb-4">
          <Col lg={2} md={4} className="mb-3">
            <Card className="stats-card border-success">
              <Card.Body>
                <div className="stats-icon text-success">
                  <i className="fas fa-hand-holding-usd"></i>
                </div>
                <div className="stats-number text-success">
                  {formatCurrency(totales.totalAportes)}
                </div>
                <div className="stats-label">Total Aportes</div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} className="mb-3">
            <Card className="stats-card border-primary">
              <Card.Body>
                <div className="stats-icon text-primary">
                  <i className="fas fa-coins"></i>
                </div>
                <div className="stats-number text-primary">
                  {formatCurrency(totales.totalCapital)}
                </div>
                <div className="stats-label">Total Capital</div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} className="mb-3">
            <Card className="stats-card border-danger">
              <Card.Body>
                <div className="stats-icon text-danger">
                  <i className="fas fa-percent"></i>
                </div>
                <div className="stats-number text-danger">
                  {formatCurrency(totales.totalInteres)}
                </div>
                <div className="stats-label">Total Interés</div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} className="mb-3">
            <Card className="stats-card border-warning">
              <Card.Body>
                <div className="stats-icon text-warning">
                  <i className="fas fa-bus"></i>
                </div>
                <div className="stats-number text-warning">
                  {formatCurrency(totales.totalPaseo)}
                </div>
                <div className="stats-label">Total Paseos</div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} className="mb-3">
            <Card className="stats-card border-info">
              <Card.Body>
                <div className="stats-icon text-info">
                  <i className="fas fa-star"></i>
                </div>
                <div className="stats-number text-info">
                  {formatCurrency(totales.totalPrograma)}
                </div>
                <div className="stats-label">Total Programa</div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={2} md={4} className="mb-3">
            <Card className="stats-card border-secondary">
              <Card.Body>
                <div className="stats-icon text-secondary">
                  <i className="fas fa-gavel"></i>
                </div>
                <div className="stats-number text-secondary">
                  {formatCurrency(totales.totalMultas)}
                </div>
                <div className="stats-label">Multas Pagadas</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tarjeta de Total General */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom border-info">
              <Card.Body>
                <Row>
                  <Col md={8}>
                    <h5 className="text-info mb-3">
                      <i className="fas fa-calculator me-2"></i>
                      Total General Recolectado
                    </h5>
                    <p className="text-muted mb-0">
                      Suma de todos los conceptos (Aportes + Capital + Interés + Paseos + Programa + Multas)
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <h2 className="text-info mb-0">
                      {formatCurrency(totales.totalGeneral)}
                    </h2>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Información de Registros */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="card-custom border-success">
              <Card.Body>
                <h6 className="text-success mb-3">
                  <i className="fas fa-check-circle me-2"></i>
                  Socios con Registro
                </h6>
                <h3 className="text-success mb-0">
                  {totales.sociosConRegistro} de {socios.length}
                </h3>
                <small className="text-muted">
                  {socios.length > 0 ? `${((totales.sociosConRegistro / socios.length) * 100).toFixed(1)}% completado` : 'N/A'}
                </small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-custom border-warning">
              <Card.Body>
                <h6 className="text-warning mb-3">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  Socios sin Registro
                </h6>
                <h3 className="text-warning mb-0">
                  {totales.sociosSinRegistro} de {socios.length}
                </h3>
                <small className="text-muted">
                  {socios.length > 0 ? `${((totales.sociosSinRegistro / socios.length) * 100).toFixed(1)}% pendiente` : 'N/A'}
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla Detallada */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-table me-2"></i>
                  Detalle de Socios - {getNombreMes(filterMes)} {filterAño}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Socio</th>
                        <th>Cédula</th>
                        <th>Número</th>
                        <th className="text-end">Aportes</th>
                        <th className="text-end">Capital</th>
                        <th className="text-end">Interés</th>
                        <th className="text-end bg-light">Cuota Préstamo</th>
                        <th className="text-end">Paseo</th>
                        <th className="text-end">Programa</th>
                        <th className="text-end">Multas</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socios.length === 0 ? (
                        <tr>
                          <td colSpan="12" className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            No hay socios registrados
                          </td>
                        </tr>
                      ) : (
                        socios.map((socio, index) => {
                          const registro = obtenerRegistroSocio(socio._id);
                          const aportes = registro?.aportes || 0;
                          const capital = registro?.capitalPrestamo || 0;
                          const interes = registro?.interesPrestamo || 0;
                          const cuotaPrestamo = capital + interes;
                          const paseo = registro?.paseo || 0;
                          const programa = registro?.programa || 0;
                          const multas = registro?.multas || 0;
                          const total = totalDeRegistro(registro);

                          return (
                            <tr key={socio._id} className={registro ? '' : 'table-light'}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                                  <div>
                                    <div className="fw-bold">{socio.name}</div>
                                    <small className="text-muted">{socio.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>{socio.cedula}</td>
                              <td>
                                <Badge bg="info">{socio.socioInfo?.numeroSocio || 'N/A'}</Badge>
                              </td>
                              <td className="text-end">
                                <span className={aportes > 0 ? 'text-success fw-bold' : 'text-muted'}>
                                  {formatCurrency(aportes)}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className={capital > 0 ? 'text-primary fw-bold' : 'text-muted'}>
                                  {formatCurrency(capital)}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className={interes > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                                  {formatCurrency(interes)}
                                </span>
                              </td>
                              <td className="text-end bg-light">
                                <span className={cuotaPrestamo > 0 ? 'text-info fw-bold' : 'text-muted'}>
                                  {formatCurrency(cuotaPrestamo)}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className={paseo > 0 ? 'text-warning fw-bold' : 'text-muted'}>
                                  {formatCurrency(paseo)}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className={programa > 0 ? 'text-info fw-bold' : 'text-muted'}>
                                  {formatCurrency(programa)}
                                </span>
                              </td>
                              <td className="text-end">
                                <span className={multas > 0 ? 'text-secondary fw-bold' : 'text-muted'}>
                                  {formatCurrency(multas)}
                                </span>
                              </td>
                              <td className="text-end">
                                <strong className={total > 0 ? 'text-info' : 'text-muted'}>
                                  {formatCurrency(total)}
                                </strong>
                              </td>
                              <td className="text-center">
                                {registro ? (
                                  <Badge bg="success">
                                    <i className="fas fa-check me-1"></i>
                                    Registrado
                                  </Badge>
                                ) : (
                                  <Badge bg="warning">
                                    <i className="fas fa-exclamation me-1"></i>
                                    Sin Registro
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td colSpan="3" className="text-end">TOTAL:</td>
                        <td className="text-end text-success">
                          {formatCurrency(totales.totalAportes)}
                        </td>
                        <td className="text-end text-primary">
                          {formatCurrency(totales.totalCapital)}
                        </td>
                        <td className="text-end text-danger">
                          {formatCurrency(totales.totalInteres)}
                        </td>
                        <td className="text-end bg-light text-info">
                          {formatCurrency(totales.totalCuotaPrestamo)}
                        </td>
                        <td className="text-end text-warning">
                          {formatCurrency(totales.totalPaseo)}
                        </td>
                        <td className="text-end text-info">
                          {formatCurrency(totales.totalPrograma)}
                        </td>
                        <td className="text-end text-secondary">
                          {formatCurrency(totales.totalMultas)}
                        </td>
                        <td className="text-end text-info">
                          {formatCurrency(totales.totalGeneral)}
                        </td>
                        <td className="text-center">
                          <Badge bg="info">{totales.sociosConRegistro}</Badge>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Resumen Descriptivo */}
        <Row className="mt-4 mb-4">
          <Col>
            <Card className="card-custom bg-light">
              <Card.Body>
                <h6 className="mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Resumen del Período
                </h6>
                <p className="mb-2">
                  <strong>Período:</strong> {getNombreMes(filterMes)} de {filterAño}
                </p>
                <p className="mb-2">
                  <strong>Total de Socios:</strong> {socios.length}
                </p>
                <p className="mb-2">
                  <strong>Socios con Registro:</strong> {totales.sociosConRegistro} ({socios.length > 0 ? `${((totales.sociosConRegistro / socios.length) * 100).toFixed(1)}%` : 'N/A'})
                </p>
                <p className="mb-2">
                  <strong>Socios sin Registro:</strong> {totales.sociosSinRegistro} ({socios.length > 0 ? `${((totales.sociosSinRegistro / socios.length) * 100).toFixed(1)}%` : 'N/A'})
                </p>
                <hr />
                <p className="mb-2">
                  <strong>Total Recolectado:</strong> <span className="text-info fw-bold">{formatCurrency(totales.totalGeneral)}</span>
                </p>
                <p className="mb-2">
                  <strong>Desglose:</strong>
                </p>
                <ul className="mb-0">
                  <li><strong className="text-success">Aportes:</strong> {formatCurrency(totales.totalAportes)}</li>
                  <li><strong className="text-primary">Capital de Préstamo:</strong> {formatCurrency(totales.totalCapital)}</li>
                  <li><strong className="text-danger">Interés de Préstamo:</strong> {formatCurrency(totales.totalInteres)}</li>
                  <li><strong className="text-warning">Paseos:</strong> {formatCurrency(totales.totalPaseo)}</li>
                  <li><strong className="text-info">Programa:</strong> {formatCurrency(totales.totalPrograma)}</li>
                  <li><strong className="text-secondary">Multas pagadas:</strong> {formatCurrency(totales.totalMultas)}</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default ReporteGeneral;
